'use client';

import { cn } from '@/lib/cn';
import {
  DropdownMenuItem,
  dropdownMenuItemVariants,
  type DropdownMenuItemProps,
} from '@heidi/design-system/dropdown-menu';
import * as React from 'react';

import { NavContext, VariantContext, useIsActivePanel } from './context';

type AdaptiveMenuItemProps = DropdownMenuItemProps;

// forwardRef so `TooltipTrigger asChild` can attach to an item in either variant.
export const AdaptiveMenuItem = React.forwardRef<HTMLDivElement, AdaptiveMenuItemProps>(
  (
    {
      children,
      onClick,
      onSelect,
      onMouseDown,
      disabled,
      variant: itemVariant,
      className,
      selected,
      inset,
      'data-testid': testId,
      ...rest
    },
    ref
  ) => {
    const variant = React.useContext(VariantContext);
    const nav = React.useContext(NavContext);
    const isActivePanel = useIsActivePanel();

    if (variant === 'menu') {
      return (
        <DropdownMenuItem
          ref={ref}
          onClick={onClick}
          onSelect={onSelect}
          onMouseDown={onMouseDown}
          disabled={disabled}
          variant={itemVariant}
          selected={selected}
          inset={inset}
          className={className}
          data-testid={testId}
          {...rest}
        >
          {children}
        </DropdownMenuItem>
      );
    }

    if (!isActivePanel) return null;

    const activate = (event: React.SyntheticEvent<HTMLDivElement>) => {
      if (disabled) return;
      onClick?.(event as unknown as React.MouseEvent<HTMLDivElement>);
      // Mirror Radix: a menu item closes on select unless onSelect prevents it.
      let prevented = false;
      onSelect?.({ preventDefault: () => (prevented = true) } as unknown as Event);
      if (!prevented) nav?.close();
    };

    // A div[role=menuitem] (not <button>) so interactive children — e.g. the
    // Switch in CopySettingsPanel — don't nest a button inside a button.
    return (
      <div
        ref={ref}
        role="menuitem"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        data-disabled={disabled ? '' : undefined}
        data-testid={testId}
        onMouseDown={onMouseDown}
        onClick={activate}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            activate(event);
          }
        }}
        className={cn(
          dropdownMenuItemVariants({ variant: itemVariant, inset, selected }),
          'min-h-11 w-full', // 44px touch target for the bottom sheet
          disabled && 'cursor-default',
          className
        )}
        {...rest}
      >
        {children}
      </div>
    );
  }
);

AdaptiveMenuItem.displayName = 'AdaptiveMenuItem';
